import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Accordion, Badge, ButtonGroup } from 'react-bootstrap';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { 
  getLenderMatchSettings, 
  toggleLenderMatch, 
  getAvailableFields, 
  updateLenderCriteria, 
  deleteCriterion,
  getActiveLenderSettings
} from '../api/lenderApi';
import { getIndustryPicklistValues } from '../api/salesforceApi';
import '../styles/FieldMappings.css'; // Reuse the same styles as FieldMappings

// Debug: Check if the function is imported correctly
console.log('LenderMatch: getIndustryPicklistValues imported as:', getIndustryPicklistValues);

// List of US state codes in alphabetical order for state restrictions
const stateCodes = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'AS', 'GU', 'MP', 'PR', 'VI'
];

/**
 * LenderMatch component for configuring lender recommendation criteria
 * 
 * This component allows administrators to:
 * - Enable/disable the lender recommendation system
 * - Configure criteria for each lender
 * - Set weights and required flags for criteria
 * 
 * @returns {JSX.Element} Lender match settings component
 */
const LenderMatch = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success, error: notifyError } = useNotifications();
  
  // State variables
  const [settings, setSettings] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lenders, setLenders] = useState([]);
  const [shouldReload, setShouldReload] = useState(false);
  const [criteriaChanged, setCriteriaChanged] = useState({});
  const [activeFilter, setActiveFilter] = useState('all'); // New state for filtering
  
  // State for dynamic industry picklist values
  const [dynamicIndustries, setDynamicIndustries] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(true);
  
  // Fetch industry picklist values from Salesforce on component mount
  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        console.log('LenderMatch: Starting to fetch industries...');
        setLoadingIndustries(true);
        const industries = await getIndustryPicklistValues();
        console.log('LenderMatch: Received industries:', industries);
        setDynamicIndustries(industries);
      } catch (error) {
        console.error('LenderMatch: Error fetching industry picklist values:', error);
        // Fallback to empty array if dynamic fetch fails
        setDynamicIndustries([]);
      } finally {
        setLoadingIndustries(false);
        console.log('LenderMatch: Finished loading industries');
      }
    };
    
    fetchIndustries();
  }, []);
  
  // Check if user is admin or developer
  useEffect(() => {
    if (user && user.role !== 'super_admin' && user.role !== 'developer') {
      navigate('/');
    }
  }, [user, navigate]);
  
  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch lender match settings
        const settingsData = await getLenderMatchSettings();
        setSettings(settingsData);
        setEnabled(settingsData.enabled);
        
        // Fetch available fields for criteria
        const fields = await getAvailableFields();
        setAvailableFields(fields);
        
        // Fetch active lenders from the database
        console.log('Fetching active lenders from database for lender match...');
        
        // Get all active lenders (both financing types)
        const activeLenders = await getActiveLenderSettings();
        
        console.log(`Fetched ${activeLenders.length} active lenders from database`);
        
        // Convert database format to component format and merge with configured lenders from settings
        const mergedLenders = activeLenders.map(lender => {
          const configuredLender = settingsData.lenders.find(l => l.lenderId === lender.stringId);
          return {
            id: lender.stringId,
            name: lender.lenderName,
            financingType: lender.financingType,
            criteria: configuredLender ? configuredLender.criteria : []
          };
        });
        
        setLenders(mergedLenders);
        
        // Reset criteria changed tracking after reload
        setCriteriaChanged({});
        
        // Reset the reload flag
        if (shouldReload) {
          setShouldReload(false);
        }
      } catch (err) {
        console.error('Error fetching lender match settings:', err);
        setError('Failed to load lender match settings. Please try again later.');
        notifyError('Failed to load lender match settings');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [shouldReload]); // Only depend on shouldReload flag
  
  // Log when criteria change to help debug
  useEffect(() => {
    lenders.forEach(lender => {
      // Add null check to ensure criteria exists before filtering
      if (lender.criteria) {
        const dateAgeCriteria = lender.criteria.filter(c => 
          isDateComparisonField(c.fieldId) && c.operator === 'older_than_months'
        );
        
        if (dateAgeCriteria.length > 0) {
          console.log('Date age criteria detected:', dateAgeCriteria);
        }
      }
    });
  }, [lenders]);
  
  // Handle toggle change
  const handleToggleChange = async (e) => {
    const newEnabled = e.target.checked;
    
    try {
      setSaving(true);
      
      // Update the enabled status
      await toggleLenderMatch(newEnabled);
      
      // Update local state without a full refresh
      setEnabled(newEnabled);
      
      // Show success notification
      success(`Lender recommendations ${newEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      console.error('Error toggling lender match:', err);
      setError('Failed to update lender match status.');
      notifyError('Failed to update lender match status');
      
      // Only reload on error
      setShouldReload(true);
    } finally {
      setSaving(false);
    }
  };
  
  // Add a new criterion to a lender
  const addCriterion = (lenderId) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    
    if (lenderIndex === -1) return;
    
    // Create a new criterion with default values
    const newCriterion = {
      fieldId: '',
      fieldName: '',
      operator: '>=',
      value: '',
      weight: 5,
      required: false
    };
    
    // Add the criterion to the lender
    const updatedLenders = [...lenders];
    updatedLenders[lenderIndex].criteria.push(newCriterion);
    
    // Update state
    setLenders(updatedLenders);
    
    // Mark criteria as changed for this lender
    setCriteriaChanged(prev => ({
      ...prev,
      [lenderId]: true
    }));
  };
  
  // Update a criterion field
  const updateCriterion = (lenderId, criterionIndex, field, value) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    
    if (lenderIndex === -1) return;
    
    // Create updated lenders array
    const updatedLenders = [...lenders];
    
    // If changing the field ID, also update the field name
    if (field === 'fieldId' && value) {
      const selectedField = availableFields.find(f => f.id === value);
      
      if (selectedField) {
        updatedLenders[lenderIndex].criteria[criterionIndex] = {
          ...updatedLenders[lenderIndex].criteria[criterionIndex],
          fieldId: value,
          fieldName: selectedField.name
        };
      }
    } else if (field === 'operator') {
      // When changing operator, adjust the value as needed
      const criterion = updatedLenders[lenderIndex].criteria[criterionIndex];
      const fieldId = criterion.fieldId;
      
      // Set appropriate default values based on new operator
      let newValue = criterion.value;
      
      if (value === 'older_than_months') {
        // Default to 12 months for business start date and 252 months (21 years) for DOB
        newValue = fieldId === 'BusinessStartDate' ? '12' : fieldId === 'DateOfBirth' ? '252' : '12';
      } else if (isDateComparisonField(fieldId) && criterion.operator === 'older_than_months') {
        // If switching from older_than_months to something else for a date field, reset value
        newValue = '';
      } else if (fieldId === 'Industry' && value === 'is_not') {
        // Initialize with empty array for Industry with is_not operator
        newValue = [];
      } else if (fieldId === 'Industry' && criterion.operator === 'is_not') {
        // If switching away from is_not for Industry, reset to empty string
        newValue = '';
      } else if (isStateField(fieldId) && value === 'is_not') {
        // Initialize with empty array for State fields with is_not operator
        newValue = [];
      } else if (isStateField(fieldId) && criterion.operator === 'is_not') {
        // If switching away from is_not for State fields, reset to empty string
        newValue = '';
      } else if (value === 'is_not' && !fieldId.includes('Industry') && !isStateField(fieldId)) {
        // Initialize with empty array for other string fields with is_not operator
        newValue = [];
      } else if (criterion.operator === 'is_not' && value !== 'is_not' && !fieldId.includes('Industry') && !isStateField(fieldId)) {
        // If switching away from is_not for other string fields, reset to empty string
        newValue = '';
      }
      
      updatedLenders[lenderIndex].criteria[criterionIndex] = {
        ...criterion,
        operator: value,
        value: newValue
      };
    } else {
      // Otherwise just update the specified field
      updatedLenders[lenderIndex].criteria[criterionIndex] = {
        ...updatedLenders[lenderIndex].criteria[criterionIndex],
        [field]: value
      };
    }
    
    // Update state
    setLenders(updatedLenders);
    
    // Mark criteria as changed for this lender
    setCriteriaChanged(prev => ({
      ...prev,
      [lenderId]: true
    }));
  };
  
  // Remove a criterion
  const removeCriterion = (lenderId, criterionIndex) => {
    const lenderIndex = lenders.findIndex(l => l.id === lenderId);
    
    if (lenderIndex === -1) return;
    
    // Create updated lenders array
    const updatedLenders = [...lenders];
    updatedLenders[lenderIndex].criteria.splice(criterionIndex, 1);
    
    // Update state
    setLenders(updatedLenders);
    
    // Mark criteria as changed for this lender
    setCriteriaChanged(prev => ({
      ...prev,
      [lenderId]: true
    }));
  };
  
  // Save changes for a specific lender
  const saveLenderCriteria = async (lenderId) => {
    const lender = lenders.find(l => l.id === lenderId);
    
    if (!lender) return;
    
    try {
      setSaving(true);
      
      // Filter out any criteria with empty fieldId
      const validCriteria = lender.criteria.filter(c => c.fieldId);
      
      // Update the criteria in the backend
      await updateLenderCriteria(lender.id, lender.name, validCriteria);
      
      // Reset change tracking for this lender
      setCriteriaChanged(prev => ({
        ...prev,
        [lenderId]: false
      }));
      
      // Trigger a single reload of data
      setShouldReload(true);
      
      // Show success notification
      success(`Criteria updated for ${lender.name}`);
    } catch (err) {
      console.error('Error saving lender criteria:', err);
      setError(`Failed to save criteria for ${lender.name}.`);
      notifyError(`Failed to save criteria for ${lender.name}`);
    } finally {
      setSaving(false);
    }
  };
  
  // List of field IDs that contain numeric values but are stored as text
  const numericTextFields = [
    'AverageBankBalance',
    'AnnualRevenue',
    'AverageMonthlySales',
    'AverageNSFs',
    'AverageNumberOfDeposits',
    'RequestedAmount',
    'ContactOwnershipPercentage',
    'CumulativeOwnershipPercentage',
    'FederalTaxID',
    'NumberOfPositions',
    'PayoffAmountNeeded',
    'ContactCreditScore',
    // Add any other fields that should be treated as numbers
  ];
  
  // Helper function to determine if a field should use numeric operators
  const shouldUseNumericOperators = (fieldId, type) => {
    // If it's already a number type, return true
    if (type === 'number') return true;
    
    // If it's in our list of numeric text fields, return true
    return numericTextFields.includes(fieldId);
  };
  
  // Helper function to check if it's a field that should have the "Older than" operator
  const isDateComparisonField = (fieldId) => {
    return fieldId === 'BusinessStartDate' || fieldId === 'DateOfBirth';
  };
  
  // Helper function to check if it's a state field
  const isStateField = (fieldId) => {
    return fieldId === 'BusinessState' || fieldId === 'ContactState' || fieldId === 'Contact2State';
  };
  
  // Get field type options based on field type
  const getFieldOptions = (fieldId) => {
    const field = availableFields.find(f => f.id === fieldId);
    
    if (!field) return [];
    
    const type = field.type.toLowerCase();
    
    // Special case for Industry field
    if (fieldId === 'Industry') {
      return [
        { value: '=', label: 'Equals' },
        { value: 'contains', label: 'Contains' },
        { value: 'is_not', label: 'Is Not One Of' }
      ];
    }
    
    // Special case for state fields
    if (isStateField(fieldId)) {
      return [
        { value: '=', label: 'Equals' },
        { value: 'is_not', label: 'Is Not One Of' }
      ];
    }
    
    // Special case for BusinessStartDate and DateOfBirth
    if (isDateComparisonField(fieldId)) {
      return [
        { value: '>', label: 'After' },
        { value: '>=', label: 'On or after' },
        { value: '=', label: 'On' },
        { value: '<=', label: 'On or before' },
        { value: '<', label: 'Before' },
        { value: 'older_than_months', label: 'Older than (months)' }
      ];
    } else if (shouldUseNumericOperators(fieldId, type)) {
      return [
        { value: '>', label: 'Greater than' },
        { value: '>=', label: 'Greater than or equal to' },
        { value: '=', label: 'Equals' },
        { value: '<=', label: 'Less than or equal to' },
        { value: '<', label: 'Less than' }
      ];
    } else if (type === 'string') {
      return [
        { value: 'contains', label: 'Contains' },
        { value: '=', label: 'Equals' },
        { value: 'is_not', label: 'Is Not One Of' }
      ];
    } else if (type === 'date') {
      return [
        { value: '>', label: 'After' },
        { value: '>=', label: 'On or after' },
        { value: '=', label: 'On' },
        { value: '<=', label: 'On or before' },
        { value: '<', label: 'Before' },
        { value: 'older_than_months', label: 'Older than (months)' }
      ];
    } else if (type === 'boolean') {
      return [
        { value: '=', label: 'Equals' }
      ];
    }
    
    return [
      { value: '=', label: 'Equals' }
    ];
  };
  
  // Get appropriately typed input for the field
  const getFieldInput = (fieldId, value, onChange, operator) => {
    const field = availableFields.find(f => f.id === fieldId);
    
    if (!field) return <Form.Control type="text" value={value} onChange={onChange} placeholder="Enter value" />;
    
    const type = field.type.toLowerCase();
    
    // Special handling for Industry with 'is_not' operator (multi-select)
    if (fieldId === 'Industry' && operator === 'is_not') {
      // Convert the value to an array if it's not already
      const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
      
      // Convert the selected values to the format needed for react-select
      const selectedOptions = selectedValues.map(val => ({ value: val, label: val }));
      
      // Create options from the dynamic industries list
      const industryOptions = dynamicIndustries.map(industry => ({
        value: industry,
        label: industry
      }));
      
      return (
        <Select
          isMulti
          options={industryOptions}
          value={selectedOptions}
          onChange={(selected) => {
            // Extract just the values from the selected options
            const newValues = selected ? selected.map(option => option.value) : [];
            // Pass the array of values to the updateCriterion function
            onChange({ target: { value: newValues } });
          }}
          placeholder="Select industries to exclude"
          styles={selectStyles}
          menuPortalTarget={document.body}
          className="select-field"
        />
      );
    }
    
    // Special handling for State fields with 'is_not' operator (multi-select)
    if (isStateField(fieldId) && operator === 'is_not') {
      // Convert the value to an array if it's not already
      const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
      
      // Convert the selected values to the format needed for react-select
      const selectedOptions = selectedValues.map(val => ({ value: val, label: val }));
      
      // Create options from the stateCodes list
      const stateOptions = stateCodes.map(state => ({
        value: state,
        label: state
      }));
      
      return (
        <Select
          isMulti
          options={stateOptions}
          value={selectedOptions}
          onChange={(selected) => {
            // Extract just the values from the selected options
            const newValues = selected ? selected.map(option => option.value) : [];
            // Pass the array of values to the updateCriterion function
            onChange({ target: { value: newValues } });
          }}
          placeholder="Select states to exclude"
          styles={selectStyles}
          menuPortalTarget={document.body}
          className="select-field"
        />
      );
    }
    
    // Special handling for State fields with '=' operator (single select)
    if (isStateField(fieldId) && operator === '=') {
      // Create options from the stateCodes list
      const stateOptions = [
        { value: '', label: 'Select a state' },
        ...stateCodes.map(state => ({
          value: state,
          label: state
        }))
      ];
      
      return (
        <Form.Select
          value={value}
          onChange={onChange}
        >
          {stateOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      );
    }
    
    // Special handling for other string fields with 'is_not' operator (multi-select)
    if (type === 'string' && operator === 'is_not' && fieldId !== 'Industry' && !isStateField(fieldId)) {
      // Convert the value to an array if it's not already
      const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
      
      // Convert the selected values to the format needed for react-select
      const selectedOptions = selectedValues.map(val => ({ value: val, label: val }));
      
      // Create common options based on field type
      let fieldOptions = [];
      
      // Add specific options for known fields
      if (fieldId === 'BusinessEntityType' || fieldId === 'EntityType') {
        fieldOptions = [
          { value: 'Sole Proprietorship', label: 'Sole Proprietorship' },
          { value: 'Partnership', label: 'Partnership' },
          { value: 'LLC', label: 'LLC' },
          { value: 'Corporation', label: 'Corporation' },
          { value: 'S-Corp', label: 'S-Corp' },
          { value: 'C-Corp', label: 'C-Corp' },
          { value: 'Non-Profit', label: 'Non-Profit' }
        ];
      } else {
        // For other fields, allow free text input
        fieldOptions = selectedOptions; // Start with existing selections
      }
      
      // Use CreatableSelect for fields that allow custom values, regular Select for predefined options
      const SelectComponent = (fieldId === 'BusinessEntityType' || fieldId === 'EntityType') ? Select : CreatableSelect;
      
      const selectProps = {
        isMulti: true,
        options: fieldOptions,
        value: selectedOptions,
        onChange: (selected) => {
          // Extract just the values from the selected options
          const newValues = selected ? selected.map(option => option.value) : [];
          // Pass the array of values to the updateCriterion function
          onChange({ target: { value: newValues } });
        },
        placeholder: `Select values to exclude from ${field.name || fieldId}`,
        styles: selectStyles,
        menuPortalTarget: document.body,
        className: "select-field"
      };
      
      // Add formatCreateLabel for CreatableSelect
      if (SelectComponent === CreatableSelect) {
        selectProps.formatCreateLabel = (inputValue) => `Add "${inputValue}"`;
      }
      
      return <SelectComponent {...selectProps} />;
    }
    
    // Special input for "Older than" operator for date fields
    if (isDateComparisonField(fieldId) && operator === 'older_than_months') {
      // Create options for 1-36 months
      const monthOptions = Array.from({ length: 36 }, (_, i) => i + 1);
      
      return (
        <Form.Select 
          value={value} 
          onChange={onChange}
          placeholder="Select months"
        >
          <option value="">Select months</option>
          {monthOptions.map(months => (
            <option key={months} value={months}>
              {months} {months === 1 ? 'month' : 'months'}
            </option>
          ))}
        </Form.Select>
      );
    } else if (shouldUseNumericOperators(fieldId, type)) {
      return (
        <Form.Control 
          type="number" 
          value={value} 
          onChange={onChange} 
          placeholder="Enter numeric value" 
        />
      );
    } else if (type === 'string') {
      return (
        <Form.Control 
          type="text" 
          value={value} 
          onChange={onChange} 
          placeholder="Enter text value" 
        />
      );
    } else if (type === 'date') {
      return (
        <Form.Control 
          type="date" 
          value={value} 
          onChange={onChange} 
        />
      );
    } else if (type === 'boolean') {
      return (
        <Form.Select value={value} onChange={onChange}>
          <option value="true">True</option>
          <option value="false">False</option>
        </Form.Select>
      );
    }
    
    return <Form.Control type="text" value={value} onChange={onChange} placeholder="Enter value" />;
  };
  
  // Group fields by category for better organization
  const fieldsByCategory = availableFields.reduce((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = [];
    }
    acc[field.category].push(field);
    return acc;
  }, {});
  
  // Create field options for Select component
  const fieldOptions = Object.entries(fieldsByCategory).map(([category, fields]) => ({
    label: category,
    options: fields.map(field => ({
      value: field.id,
      label: field.name,
      type: field.type
    }))
  }));
  
  // Add this at the top level with other constant definitions
  const selectStyles = {
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999
    })
  };
  
  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading lender match settings...</p>
      </Container>
    );
  }
  
  return (
    <Container className="my-5">
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="align-items-center mb-4">
            <Col>
              <h2 className="mb-0">Lender Match Settings</h2>
              <p className="text-muted mt-1">
                Configure criteria to recommend the best lenders for each contact
              </p>
            </Col>
            <Col xs="auto">
              <div className="d-flex align-items-center">
                <Form.Check
                  type="switch"
                  id="lender-match-toggle"
                  label={enabled ? "Enabled" : "Disabled"}
                  checked={enabled}
                  onChange={handleToggleChange}
                  disabled={saving}
                  className="fs-5 lender-match-toggle me-3"
                />
              </div>
            </Col>
          </Row>
          
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}
          
          <Alert variant="info" className="mb-4">
            <Alert.Heading>How Lender Matching Works</Alert.Heading>
            <p>
              For each lender, you can define criteria that will be evaluated against contact data.
              Each criterion has a weight (1-10) that determines its importance in the final score.
              Required criteria must be met for the lender to be considered at all.
            </p>
            <p className="mb-0">
              When enabled, match scores will be displayed in the lender submission section
              to help users choose the most appropriate lender for each contact.
            </p>
            <hr />
            <p className="mb-0">
              <strong>Note:</strong> Required criteria that don't have values filled in yet will be marked as "Pending Input" rather than causing an auto-decline. This ensures more accurate matching while users are still entering data.
            </p>
          </Alert>
          
          {/* Financing Type Filter */}
          <div className="mb-3 d-flex align-items-center">
            <span className="me-3 fw-bold">Filter Lenders:</span>
            <ButtonGroup>
              <Button 
                variant={activeFilter === 'all' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveFilter('all')}
              >
                All
              </Button>
              <Button 
                variant={activeFilter === 'working-capital' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveFilter('working-capital')}
              >
                Working Capital
              </Button>
              <Button 
                variant={activeFilter === 'equipment-financing' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveFilter('equipment-financing')}
              >
                Equipment Financing
              </Button>
            </ButtonGroup>
          </div>
          
          <Accordion className="mb-4">
            {lenders
              .filter(lender => activeFilter === 'all' || lender.financingType === activeFilter)
              .map((lender, index) => (
              <Accordion.Item key={lender.id} eventKey={lender.id}>
                <Accordion.Header>
                  <div className="d-flex align-items-center justify-content-between w-100 pe-4">
                    <span>
                      {lender.name}
                      {' '}
                      <Badge bg={lender.financingType === 'equipment-financing' ? 'success' : 'primary'} className="ms-2" style={{ fontSize: '0.7rem' }}>
                        {lender.financingType === 'equipment-financing' ? 'Equipment' : 'Working Capital'}
                      </Badge>
                    </span>
                    <Badge bg="secondary">
                      {lender.criteria.length} {lender.criteria.length === 1 ? 'criterion' : 'criteria'}
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  {lender.criteria.length === 0 ? (
                    <Alert variant="light" className="text-center mb-3">
                      No criteria defined yet. Add criteria to improve lender matching.
                    </Alert>
                  ) : (
                    <div className="mb-4">
                      {lender.criteria.map((criterion, criterionIndex) => (
                        <Card key={criterionIndex} className="mb-3 border criterion-card">
                          <Card.Body style={{ paddingBottom: '1.5rem' }}>
                            <Row className="mb-3">
                              <Col md={4}>
                                <Form.Group>
                                  <Form.Label>Field</Form.Label>
                                  <Select
                                    options={fieldOptions}
                                    value={fieldOptions.flatMap(group => group.options).find(opt => opt.value === criterion.fieldId)}
                                    onChange={(selected) => updateCriterion(lender.id, criterionIndex, 'fieldId', selected.value)}
                                    placeholder="Select a field"
                                    isDisabled={saving}
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    className="select-field"
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={3}>
                                <Form.Group>
                                  <Form.Label>Operator</Form.Label>
                                  <Select
                                    options={getFieldOptions(criterion.fieldId)}
                                    value={criterion.fieldId ? 
                                      getFieldOptions(criterion.fieldId).find(opt => opt.value === criterion.operator) || null : null}
                                    onChange={(selected) => updateCriterion(lender.id, criterionIndex, 'operator', selected.value)}
                                    placeholder="Select operator"
                                    isDisabled={!criterion.fieldId || saving}
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    className="select-operator"
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={5}>
                                <Form.Group>
                                  <Form.Label>Value</Form.Label>
                                  {getFieldInput(
                                    criterion.fieldId,
                                    criterion.value,
                                    (e) => updateCriterion(lender.id, criterionIndex, 'value', e.target.value),
                                    criterion.operator
                                  )}
                                </Form.Group>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label>Weight (1-10)</Form.Label>
                                  <Form.Range
                                    min={1}
                                    max={10}
                                    value={criterion.weight}
                                    onChange={(e) => updateCriterion(lender.id, criterionIndex, 'weight', Number(e.target.value))}
                                    disabled={saving}
                                  />
                                  <div className="d-flex justify-content-between">
                                    <span className="text-muted">Less important</span>
                                    <span className="fw-bold">{criterion.weight}</span>
                                    <span className="text-muted">More important</span>
                                  </div>
                                </Form.Group>
                              </Col>
                              <Col md={3} className="d-flex align-items-center">
                                <Form.Check
                                  type="checkbox"
                                  id={`required-${lender.id}-${criterionIndex}`}
                                  label="Required"
                                  checked={criterion.required}
                                  onChange={(e) => updateCriterion(lender.id, criterionIndex, 'required', e.target.checked)}
                                  disabled={saving}
                                />
                              </Col>
                              <Col md={3} className="d-flex align-items-center justify-content-end">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => removeCriterion(lender.id, criterionIndex)}
                                  disabled={saving}
                                >
                                  Remove
                                </Button>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  )}
                  
                  <div className="d-flex justify-content-between">
                    <Button
                      variant="outline-primary"
                      onClick={() => addCriterion(lender.id)}
                      disabled={saving}
                    >
                      Add Criterion
                    </Button>
                    
                    <Button
                      variant="primary"
                      onClick={() => saveLenderCriteria(lender.id)}
                      disabled={saving || !criteriaChanged[lender.id]}
                    >
                      {saving ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : 'Save Criteria'}
                    </Button>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default LenderMatch; 